/**
 * MergeTagMenu — the `{ }` button (Figma, editor toolbar and subject row): a
 * menu of the tags the BE's renderer knows (render.KnownTags) that inserts the
 * chosen `{tag}` into the field it belongs to. `{sender}` is a name-only tag
 * for greetings and body text, never a sign-off — the signature is appended
 * at send time (U3).
 */
import { BracketsCurly } from '../../../design-system/icons';
import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { KNOWN_MERGE_TAGS, type KnownMergeTag } from './templateValidation';

export const MERGE_TAG_LABELS: Record<KnownMergeTag, string> = {
  first_name: 'First name',
  brand: 'Brand',
  campaign: 'Campaign',
  sender: 'Sender first name',
  followers: 'Followers',
  last_rate: 'Last rate',
  year: 'Campaign year',
  creator_name: 'Creator name',
};

export interface MergeTagMenuProps {
  /** Receives the full `{tag}` text, ready to splice in at the caret. */
  onInsert: (tag: string) => void;
  /** Accessible name of the trigger, naming the field it inserts into. */
  label: string;
  disabled?: boolean;
}

export function MergeTagMenu({ onInsert, label, disabled = false }: MergeTagMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          title="Insert merge tag"
          aria-label={label}
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          className="rounded-lg text-(--icon-default)"
        >
          <BracketsCurly size={20} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {KNOWN_MERGE_TAGS.map((tag) => (
          <DropdownMenuItem key={tag} onSelect={() => onInsert(`{${tag}}`)}>
            <span>{MERGE_TAG_LABELS[tag]}</span>
            <span className="ml-auto font-mono text-[11px] text-muted-foreground">{`{${tag}}`}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
